<template>
  <div id="app">
    <div class="container">
      <!-- Lista de receituários do paciente -->
      <b-card v-if="!showReceituarioForm && !loading">
        <b-card-body>
          <div v-if="receituarios.length === 0" class="text-center py-4">
            <feather-icon
              icon="FileTextIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">
              Nenhum receituário encontrado para este paciente.
            </p>
            <b-button variant="primary" @click="createReceituario">
              Criar Primeiro Receituário
            </b-button>
          </div>

          <b-list-group v-else>
            <div class="justify-content-end d-flex mb-2 flex-wrap">
              <b-button variant="primary" @click="createReceituario">
                Criar Novo Receituário
              </b-button>
            </div>
            <b-list-group-item
              v-for="receituario in receituarios"
              :key="receituario.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h6 class="mb-1">Receituário #{{ receituario.id }}</h6>
                <small>
                  Data: {{ formatHyphenToSlash(receituario.dataReceita) }}
                </small>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  title="Editar Receituário"
                  @click="editReceituario(receituario)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- Loading spinner -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Carregando..." />
        <p class="mt-2 text-muted">Carregando receituários...</p>
      </div>

      <!-- Formulário de odontograma (será mostrado quando showOdontogramaForm = true) -->
      <div
        v-if="showReceituarioForm"
        class="justify-content-end d-flex flex-wrap"
      >
        <b-button
          class="mb-3"
          variant="outline-secondary"
          @click="backToList()"
        >
          <feather-icon icon="ArrowLeftIcon" />
          Voltar para Lista
        </b-button>
        <DigitalReceituario
          :receituario-props="receituario"
          @back-list="backToList()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BButton,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import DigitalReceituario from "./components/DigitalReceituario.vue";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    BBadge,
    DigitalReceituario,
  },
  props: {
    atendimento: {
      type: Object,
      required: true,
    },
  },
  watch: {
    "atendimento.id": {
      handler() {
        this.loadPatientPareceres();
      },
      immediate: true,
    },
  },
  data() {
    return {
      showReceituarioForm: false,
      loading: false,
      receituarios: [],
      patientId: null,
      currentDate: this.formatDateToDDMMYYYY(new Date()),
      receituario: [],
    };
  },
  methods: {
    backToList() {
      this.loadPatientPareceres();
      this.showReceituarioForm = false;
    },
    /**
     * Formata a data para o formato dd-mm-yyyy
     */
    formatDateToDDMMYYYY(date) {
      if (!date) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    /**
     * Transforma uma data no formato DD-MM-YYYY para DD/MM/YYYY
     */
    formatHyphenToSlash(dateString) {
      if (!dateString) return "";
      try {
        // Verifica se a data está no formato esperado DD-MM-YYYY
        if (
          typeof dateString !== "string" ||
          !/^\d{2}-\d{2}-\d{4}$/.test(dateString)
        ) {
          console.warn("Data não está no formato DD-MM-YYYY:", dateString);
          return dateString;
        }

        // Substitui os hifens por barras
        return dateString.replace(/-/g, "/");
      } catch (error) {
        console.error("Erro ao transformar formato da data:", error);
        return "";
      }
    },

    /**
     * Carrega os odontogramas do paciente usando o serviço getPatientById
     */
    async loadPatientPareceres() {
      this.patientId = this.atendimento.registro.id;
      if (!this.patientId) {
        return;
      }

      this.loading = true;
      try {
        // Usar o serviço diretamente ou através do Vuex
        const response = await this.$store.dispatch(
          "receituarioModule/getPatientById",
          this.patientId
        );
        this.receituarios = (response || response.data || []).sort(
          (a, b) => b.id - a.id
        );
      } catch (error) {
        console.error("Erro ao carregar receituarios do paciente:", error);
        this.$bvToast.toast("Erro ao carregar receituarios do paciente", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    /**
     * Formata a data para exibição
     */
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    /**
     * Edita um receituário existente
     */
    editReceituario(receituario) {
      this.loading = true;
      this.$store
        .dispatch("receituarioModule/getById", receituario.id)
        .then((response) => {
          this.receituario = response;
          this.showReceituarioForm = true;
          this.loading = false;
        })
        .catch((error) => {
          console.error("Erro ao carregar receituário:", error);
          this.loading = false;
        });
    },

    createReceituario() {
      this.receituario = {
        atendimento: this.atendimento.id,
        ativo: true,
        dataReceita: new Date().toISOString().split("T")[0],
        especialidade: this.atendimento.especialidade,
        id: null,
        observacao: null,
        paciente: this.atendimento.registro,
        prescricao: null,
        profissional: this.atendimento.profissional,
        versao: null,
      };
      this.showReceituarioForm = true;
    },

    /**
     * Atualiza um dente quando o componente DigitalOdontogram emite o evento
     */
    updateReceituario(updatedReceituario) {
      const index = this.receituarios.findIndex(
        (receituario) => receituario.id === updatedReceituario.id
      );
      if (index !== -1) {
        this.receituarios.splice(index, 1, updatedReceituario);
      }
    },
  },
};
</script>
